import { Alert, Box, Tabs, Tab, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import TabPanel from 'src/components/TabPanel';

import VendorBoothSettings from './vendorBoothSettings';
import VendorBoothContent from './vendorBoothContent';
import VendorBoothContact from './vendorBoothContact';
import useVendorBooth from 'src/hooks/useVendorBooth';
import useVendor from 'src/hooks/useVendor';
import UmbarcoApi from 'src/api/UmbarcoApi';
import LoadingButton from '@mui/lab/LoadingButton';
import AddIcon from '@mui/icons-material/Add';

import ShowTour from '../ShowTour';
import useOnMount from 'src/hooks/useOnMount';
import { LOCAL_STORAGE_KEYS } from 'src/constants';
import LoginApi from 'src/api/LoginApi';
import VendorApi from 'src/api/VendorApi';

declare interface Props {}

const VendorBooth: React.FC<React.PropsWithChildren<Props>> = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const { loadBooth, resetBoothToDefault } = useVendorBooth();
  const { vendor, setSelectedBoothId, setVendorBoothsDropdown } = useVendor();
  const [createBoothLoading, setCreateBoothLoading] = useState(false);
  const [boothLink, setBoothLink] = useState<string>('');
  const [lastYearsBoothId, setLastYearsBoothId] = useState<number>(0);
  const [lastYearBoothLoading, setLastYearBoothLoading] = useState(false);
  const { vendorBooth } = useVendorBooth();
  const [isViewBoothLoading, setIsViewBoothLoading] = useState<boolean>(false);
  const [portalSettingsLoaded, setPortalSettingsLoaded] = useState(false);
  const [allowVirtualBooth, setAllowVirtualBooth] = useState(true);

  const handleChange = (e, newIndex) => {
    setTabIndex(newIndex);
  };

  useOnMount(() => {
    if (vendor?.number) {
      setLastYearBoothLoading(true);
      UmbarcoApi.getLastYearsBooths(vendor.number)
        .then((booths) => {
          if (booths && booths.length > 0) {
            setLastYearsBoothId(booths[0].id);
          }
        })
        .finally(() => {
          setLastYearBoothLoading(false);
        });
    }
  });

  useEffect(() => {
    if (!vendor?.numberString) {
      return;
    }

    let cancelled = false;
    VendorApi.getVendorPortalSettings(vendor.numberString)
      .then((settings) => {
        if (!cancelled) {
          setAllowVirtualBooth(settings.allowVirtualBooth);
          setPortalSettingsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAllowVirtualBooth(true);
          setPortalSettingsLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [vendor?.numberString]);

  useEffect(() => {
    if (vendorBooth && vendorBooth.boothProperties) {
      let showId = vendorBooth.showId;
      if (!showId) return;
      let boothNumber: string = vendorBooth.boothProperties?.find((x) => x.alias === 'boothNumber')?.value?.toString();
      if (!boothNumber) return;
      //remove an apmersand from the booth number
      boothNumber = encodeURIComponent(boothNumber);

      let boothUrl = `https://vexpos.bfgsupply.com/show/${showId}/booth/${boothNumber}`;
      setBoothLink(boothUrl);
    }
  }, [vendorBooth, vendorBooth.boothProperties, vendorBooth.showId]);

  useEffect(() => {
    if (
      vendor &&
      (vendor.selectedBoothId == null || vendor.selectedBoothId == 0 || vendor.selectedBoothId == undefined) &&
      vendor.boothsDropdown &&
      vendor.boothsDropdown.length > 0
    ) {
      setSelectedBoothId(vendor.boothsDropdown[0].boothId);
    }

    if (vendor.selectedBoothId != null && vendor.selectedBoothId != 0) {
      loadBooth(vendor.selectedBoothId, vendor.number);
    }
  }, [vendor, vendor.number, vendor.selectedBoothId, vendor.boothsDropdown, setSelectedBoothId, loadBooth]);

  const showBooth: Boolean = vendor.selectedBoothId && vendor.boothsDropdown && vendor.boothsDropdown.length > 0 && vendorBooth.isLoading === false;

  const virtualBoothCreationBlocked = !portalSettingsLoaded || !allowVirtualBooth;
  const virtualBoothCreationDisabledMessage = portalSettingsLoaded && !allowVirtualBooth;

  const createNewBooth = () => {
    setCreateBoothLoading(true);
    resetBoothToDefault();
    UmbarcoApi.createNewBooth(vendor.numberString, vendor.name)
      .then((booth) => {
        setVendorBoothsDropdown([booth]);
      })
      .finally(() => {
        setCreateBoothLoading(false);
      });
  };

  const createBoothFromLastYear = () => {
    setCreateBoothLoading(true);

    UmbarcoApi.createNewBoothFromLastYear(vendor.numberString, vendor.name, lastYearsBoothId)
      .then((booth) => {
        setVendorBoothsDropdown([booth]);
      })
      .finally(() => {
        setCreateBoothLoading(false);
      });
  };

  const handleTourChange = (newIndex) => {
    setTabIndex(newIndex);
  };

  const onViewBoothClicked = () => {
    if (boothLink !== '') {
      let username = localStorage.getItem(LOCAL_STORAGE_KEYS.UserName);
      let password = localStorage.getItem(LOCAL_STORAGE_KEYS.Password);

      if (username && password) {
        //login in on webapi
        setIsViewBoothLoading(true);
        LoginApi.loginWebApi(username, password)
          .then(() => {
            console.log('login to WebApi Success');
          })
          .catch((err) => {
            console.error('login to WebApi Failed', err);
          })
          .finally(() => {
            setIsViewBoothLoading(false);
            window.open(boothLink, '_blank');
          });
      } else {
        window.open(boothLink, '_blank');
      }
    }
  };

  return (
    <div>
      <Box>
        {vendorBooth.isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '50px' }}>
            <CircularProgress />
          </div>
        )}

        {showBooth ? (
          <>
            <Tabs value={tabIndex} onChange={handleChange} aria-label="Tabs" sx={{ mb: 5, borderBottom: 1, borderColor: 'divider', width: '100%' }}>
              <Tab label="Settings" />
              <Tab label="Key Items" />
              <Tab label="Contact" />
              {boothLink !== '' && (
                <LoadingButton variant="contained" loading={isViewBoothLoading} onClick={onViewBoothClicked} style={{ position: 'absolute', right: 0, top: 5 }}>
                  View Booth
                </LoadingButton>
              )}
            </Tabs>
          </>
        ) : vendor.boothsDropdown == null || vendor.boothsDropdown.length == 0 ? (
          <div className="new-vendor-booth">
            <p className="new-vendor-booth__title">No booths for this vendor</p>
            <p className="new-vendor-booth__subtitle">Click below to create a new booth. </p>
            {virtualBoothCreationDisabledMessage && (
              <Alert severity="warning" sx={{ maxWidth: 560, my: 2, mx: 'auto' }}>
                Virtual booth creation is currently disabled for this account. Please contact{' '}
                <a href="mailto:events@bfgsupply.com">events@bfgsupply.com</a>.
              </Alert>
            )}
            <LoadingButton
              onClick={createNewBooth}
              title="Create a new booth and start from scratch with no data."
              endIcon={<AddIcon />}
              loading={createBoothLoading}
              loadingPosition="end"
              variant="contained"
              disabled={virtualBoothCreationBlocked}
            >
              <span>Create New Booth</span>
            </LoadingButton>
            {(lastYearsBoothId > 0 || lastYearBoothLoading === true) && (
              <>
                <br />
                <LoadingButton
                  disabled={lastYearBoothLoading || virtualBoothCreationBlocked}
                  onClick={createBoothFromLastYear}
                  style={{ marginTop: '25px' }}
                  title="Create a new booth using last years data as a starting point."
                  endIcon={<AddIcon />}
                  loading={createBoothLoading || lastYearBoothLoading}
                  loadingPosition="end"
                  variant="contained"
                >
                  <span>Create Copy From Last Year's Booth</span>
                </LoadingButton>
              </>
            )}
            {createBoothLoading && (
              <div className="new-vendor-booth__loading-text">
                {/* <CircularProgress></CircularProgress> <br /> */}
                <span>Creating booth, this may take a minute...</span>
              </div>
            )}
          </div>
        ) : (
          <>{(vendor.selectedBoothId == null || vendor.selectedBoothId == 0) && <div>There is no selected booth</div>}</>
        )}

        <>
          {showBooth && (
            <>
              <ShowTour
                id="vendorBoothTour"
                steps={[
                  {
                    target: '.field-group__booth-content',
                    content: 'This is the general booth content area',
                    disableBeacon: true,
                    locale: { skip: 'Close Tour' },
                  },
                  {
                    target: '.field-group__booth-media',
                    content: 'Add images and links to media, here',
                    disableBeacon: true,
                    locale: { skip: 'Close Tour' },
                  },
                  {
                    target: '.repeater__add-item',
                    content: 'Add a new item, here',
                    disableBeacon: true,
                    locale: { skip: 'Close Tour' },
                  },
                  {
                    target: '.repeater-item:first-child',
                    content: 'Hover over an item to see item detail or change item selection',
                    disableBeacon: true,
                    locale: { skip: 'Close Tour', last: 'End Tour' },
                  },
                ]}
                tabIndex={tabIndex}
                onTabChange={handleTourChange}
              />
              <TabPanel value={tabIndex} index={0}>
                <VendorBoothSettings />
              </TabPanel>
              <TabPanel value={tabIndex} index={1}>
                <VendorBoothContent />
              </TabPanel>
              <TabPanel value={tabIndex} index={2}>
                <VendorBoothContact />
              </TabPanel>
            </>
          )}
        </>
      </Box>
    </div>
  );
};

export default VendorBooth;
